import { useSyncExternalStore } from 'react'
import { subscribeToPush, webPushStatus } from '../system/webPush'

/**
 * The long-run notice's state, held outside the cards: the card that raised the browser
 * prompt is gone the moment its run ends, and a later card must know notifications are
 * already on rather than asking again. "On" lives in the browser's push subscription, so it
 * survives a reload (`syncLongRunNotice`); the presses and "Don't ask again" are memory only.
 */

/** How long a job runs before its card offers to notify (D-p3-687). */
export const LONG_RUN_AFTER_MS = 10_000

export type NoticePress = 'notify' | 'dismiss'

export interface LongRunNoticeState {
  /** This browser can be notified: push works here and the server is set up for it. */
  available: boolean
  /** This browser is subscribed: the trader allowed notifications, now or on an earlier visit. */
  enabled: boolean
  /** The AI has said in chat that notifications are on (once per session). */
  announced: boolean
  /** Notices whose trigger has been used, by notice id. */
  pressed: Record<string, NoticePress>
  /** "Don't ask again": later notices keep their line but drop the ask. */
  dismissedAsk: boolean
}

const initial: LongRunNoticeState = { available: false, enabled: false, announced: false, pressed: {}, dismissedAsk: false }

let state = initial
const listeners = new Set<() => void>()

function set(next: Partial<LongRunNoticeState>) {
  state = { ...state, ...next }
  listeners.forEach((l) => l())
}

export const longRunNoticeStore = {
  get: () => state,
  subscribe(listener: () => void) {
    listeners.add(listener)
    return () => {
      listeners.delete(listener)
    }
  },
  press(id: string, how: NoticePress) {
    set({ pressed: { ...state.pressed, [id]: how } })
  },
  enable() {
    set({ enabled: true })
  },
  /** Found subscribed on load: on, and already said so on the visit that turned it on. */
  restore(available: boolean, subscribed: boolean) {
    set({ available, enabled: subscribed, announced: state.announced || subscribed })
  },
  dismissAsk(id: string) {
    set({ dismissedAsk: true, pressed: { ...state.pressed, [id]: 'dismiss' } })
  },
  markAnnounced() {
    set({ announced: true })
  },
  reset() {
    state = initial
    listeners.forEach((l) => l())
  },
}

export function useLongRunNotice() {
  return useSyncExternalStore(longRunNoticeStore.subscribe, longRunNoticeStore.get)
}

/** Reads whether this browser is already subscribed; run once when the signed-in app loads. */
export async function syncLongRunNotice() {
  try {
    const { available, subscribed } = await webPushStatus()
    longRunNoticeStore.restore(available, subscribed)
  } catch (error) {
    console.error('Push status check failed', error)
  }
}

/**
 * Raises the browser's own permission prompt and subscribes this browser. Allow turns
 * notifications on; Block or a dismissed prompt leaves the next long wait free to ask again.
 */
export async function requestNotifications() {
  try {
    const subscribed = await subscribeToPush()
    if (subscribed) longRunNoticeStore.enable()
    return subscribed
  } catch (error) {
    console.error('Push subscription failed', error)
    return false
  }
}
